/**
 * The refusal every backend answers an archived tenant's own users with
 * (#1379): 403 and `code: 'tenant_archived'`. Same string as
 * `TENANT_ARCHIVED_CODE` in `shared/src/tenantArchive.ts` and
 * `ACCOUNT_USER_ERROR_CODES.TENANT_ARCHIVED` in new-backend/admin; this
 * workspace cannot import either, so `tenantArchived.test.ts` reads the
 * backend file and pins the two together.
 */
export const TENANT_ARCHIVED_CODE = 'tenant_archived';

/**
 * Is this RTK Query error (`{ status, data }`) the archived-tenant refusal?
 * new-backend sends `{ code, message }`, the Lambda backend `{ code, msg }`;
 * both parse to an object with `code`.
 */
export const isTenantArchivedError = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') return false;
    const { status, data } = error as { status?: unknown, data?: unknown };
    if (status !== 403 || !data || typeof data !== 'object') return false;
    const code = (data as { code?: unknown }).code;
    return typeof code === 'string' && code.toLowerCase() === TENANT_ARCHIVED_CODE;
};

/**
 * Days an archived account is kept, and may be reactivated. Copy of
 * `ARCHIVE_RETENTION_DAYS` in `shared/src/tenantArchive.ts` (pinned by
 * `tenantArchived.test.ts`). Only for wording before the server has answered;
 * the dates themselves always come from the server (`deletionDueAt`).
 */
export const ARCHIVE_RETENTION_DAYS = 30;

/**
 * Should the "This account has been closed" screen replace the app? Yes when
 * the refusal was raised before a tenant was resolved ('' — the boot path) or
 * for the tenant on screen; a refusal for another tenant (a login with several
 * memberships that switched away) does not lock this one.
 */
export const showsAccountClosed = (closedTenant: string | null, tenant: string | null): boolean =>
    closedTenant !== null && (closedTenant === '' || closedTenant === tenant);
